/**
 * Dedupe key for `storage_on_demand_card_view` (the on-demand discovery
 * denominator — see `onDemandCardView` in `spotmeEvents.ts`).
 *
 * The intent is ONE view per (surface, city) per PostHog SESSION, because the
 * numerator (`storage_on_demand_card_click`) is joined to it on `$session_id`
 * to compute card CTR. Keying on `sessionStorage` alone outlived that: a tab
 * that idles past PostHog's 30-min session rollover keeps the old
 * `sessionStorage` entry, so the next session's click has no view in the same
 * `$session_id` (measured 2026-09-14: 63 click-sessions in 30 days with no
 * same-session view — strict CTR 5.7% vs loose 21%). Folding the session id
 * into the key makes the dedupe window match the join window.
 *
 * Pure — no storage, no PostHog. The caller passes `posthog.sessionId()`;
 * when that is empty (PostHog not initialised yet — first paint, pre-fuse)
 * the key falls back to a 30-minute wall-clock bucket, which approximates a
 * session without waiting for the lazy SDK import.
 */

export const ON_DEMAND_CARD_VIEW_KEY_PREFIX = 'od_card_view'

/** Mirrors PostHog's default session idle timeout (30 min). */
export const SESSION_FALLBACK_BUCKET_MS = 30 * 60 * 1000

export type OnDemandCardViewKeyInput = {
  surface?: string
  city?: string
  /** `posthog.sessionId()` — empty string when PostHog is not ready. */
  sessionId?: string
  /** Injectable clock for the fallback bucket (tests). Defaults to `Date.now()`. */
  now?: number
}

export const buildOnDemandCardViewKey = ({
  surface,
  city,
  sessionId,
  now
}: OnDemandCardViewKeyInput): string => {
  const session =
    sessionId && sessionId.length > 0
      ? sessionId
      : `t${Math.floor((now ?? Date.now()) / SESSION_FALLBACK_BUCKET_MS)}`
  return `${ON_DEMAND_CARD_VIEW_KEY_PREFIX}:${surface ?? 'unknown'}:${city ?? 'none'}:${session}`
}
